import {
    all,
    call,
    delay,
    fork,
    put,
    race,
    select,
    takeLatest,
    takeMaybe,
    take,
} from 'redux-saga/effects';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import AnalyticsActionTypes from 'yoda-site-components/lib/actionTypes/AnalyticsActionTypes';
import { LOCATION_SERVICE_COOKIES_USER_PREFERRED_STORE } from 'yoda-site-components/lib/common/Constants';
import { SAVEDITEMS_GET_REQUEST } from 'yoda-site-components/lib/actionTypes/SavedItemActionTypes';
import {
    SET_USER_STORE_LOCATION_INFO,
    LOAD_USER_STORE_LOCATION_INFO_ERROR,
} from 'yoda-site-components/lib/actionTypes/LocationServiceActionTypes';
import { AB_TEST_DETAILS_SUCCESS } from 'yoda-site-components/lib/actionTypes/ABTestEngagementsActionTypes';
import {
    selectFeatureFlags,
    selectPreferences,
} from 'yoda-site-components/lib/selectors/ContextSelector';
import LocalStorage from 'yoda-core-components/lib/helpers/LocalStorage/LocalStorage';
import { getABTestEngagements } from 'yoda-interfaces/lib/ABTestEngagements/ABTestApi';
import MboxHelper from 'yoda-interfaces/lib/helpers/MboxHelper';
import { getCookie } from 'yoda-interfaces/lib/helpers/Cookies';
import Cookies from 'yoda-core-components/lib/helpers/Cookies/Cookies';
import getUserAkamaiZipCode from 'yoda-site-components/lib/selectors/LocationSelector';
import { RERENDER_COMPONENT } from 'yoda-site-components/lib/actionTypes/GalleryRerenderActionTypes';
import { getGalleryDetailsById, getZone } from 'yoda-interfaces/lib/Catalog/CategoriesApi';
import {
    getConstructorSearchResults,
    getProductAlias,
    getConstructorZone,
    getConstructorBrowseResults,
    getConstructorFacetOptions,
} from 'yoda-interfaces/lib/Search/ConstructorIO/ConstructorIOSearchApi';
import { isConstructorEnabled } from 'yoda-site-components/lib/helpers/ConstructorIO/ConstructorIO';
import isBloomReachAudience, {
    enableBloomReachPage,
} from 'yoda-site-components/lib/helpers/BloomReach/BloomReach';

import { getCriteoData, clearCriteoData } from 'yoda-site-components/lib/actions/CriteoAction';
import {
    triggerFormError,
    triggerOOSSponsoredProductFormError,
} from 'yoda-site-components/lib/actions/AnalyticsAction';
import { getLocalizationZipcode } from 'yoda-site-components/lib/helpers/Utils/utils';
import { isCookieEnabled } from 'yoda-site-components/lib/helpers/Utils/parseOnetrust';
import {
    OPTANON_COOKIE,
    TRACKING,
} from 'yoda-site-components/lib/helpers/Utils/parseOnetrustConfig.config';
import {
    transformConstructorSearchResponse,
    getConstructorIgnoreParams,
    applyRangeFilters,
    parsePreviewDateToIso,
    getPreviewCentralKey,
    selectActivePreviewWindows,
    stripPreviewSuffix,
} from '../helpers/ConstructorTransformer';
import { detectProductId, stripSpaces } from '../helpers/ProductIdDetector';
import { selectIsInitialCallClient } from '../selectors/IrisContentSelectors';
import {
    GET_FILTERS_SUCCESS,
    GET_GALLERY_DETAILS_BY_ID_ERROR,
    GET_GALLERY_DETAILS_BY_ID_REQUEST,
    GET_GALLERY_DETAILS_BY_ID_SUCCESS,
    GET_GALLERY_DETAILS_TIMEOUT_ERROR,
    GET_PRODUCT_LIST_SUCCESS,
    INITIATE_PENDING_ACTIONS,
    LAST_GALLERY_ACTION_RESULT,
    PREV_NO_RESULTS,
    RESET_COMMON_DATA,
    SPONSORED_DISPLAY_PAGE,
} from '../actionTypes/GalleryActionTypes';
import REFRESH_CONTENT from '../actionTypes/RefreshContentActionTypes';
import RERENDER_CERTONA from '../actionTypes/CertonaActionTypes';
import { HIDE_LOADER_GALLERY } from '../actionTypes/LoaderActionTypes';
import redirectRequired from '../helpers/RedirectHelper';
import Application from '../helpers/Application';
import {
    enableFilterAnalytics,
    getLastFilter,
    getPageType,
    getPageURL,
    getProductsPerPage,
    getURLParameterBykey,
    removeQueryStringParameter,
    updateUrl,
} from '../utils';
import { refreshStoreInventory } from './BOPISSaga';
import {
    exposeSkuSwatches,
    exposeEnsembleData,
    hasBreadCrumbLinks,
    getMultipleThumbnailsPpIds,
} from '../helpers/GalleryHelper';
import { selectContext } from '../selectors/ContextSelector';
import { scrollTitleIntoView } from '../utils/scrollTitleIntoView';
import getBreadCrumbInfo from '../selectors/BreadCrumbs';
import { getInteractiveVizNavFromIrisDataState } from '../selectors/InteractiveVizNav';
import { selectHideBreadCrumbsWhenNoNavigation } from '../selectors/fromState';
import { updateSelectedStoreLocationInfo } from '../actions/BOPISActions';
import {
    allProductsCategory,
    SORT_KEYS,
    PRICING_SORT_VALUES,
    PREVIEWABLE_FIELDS_KEY,
} from '../common/constants';

const logger = Application.getLogger('saga');

/**
 * Get the data from store to verify loader status
 * @param {Object}  state(Application state)
 */

let initialLoad = true;

export const getStoreContext = (state) => state.context;

export const getStoreProducts = (state) => state.products;
const getCriteo = (state) => state?.criteo;

const getFeatureFlags = (state) => selectFeatureFlags(state);

const getBOPISStore = (state) => state.locationServiceReducer;

const getIsInitialCallClient = (state) => selectIsInitialCallClient(state);

const getZipCode = (state) => getUserAkamaiZipCode(state);

const getZoneInfo = () => {
    const localStorage = LocalStorage && LocalStorage.getData('zone');
    if (localStorage) {
        return localStorage.zone;
    }
    return '';
};

const getZipInfo = () => {
    const localStorage = LocalStorage && LocalStorage.getData('zone');
    if (localStorage) {
        return localStorage.zipcode;
    }
    return '';
};

const shouldCallZoneAPI = (akamaiZipCode, zipcode) =>
    getPageType() === 's' && akamaiZipCode && (!zipcode || akamaiZipCode !== zipcode);

const productsParam = (numberOfProducts) => {
    let queryParam = null;
    if (numberOfProducts) {
        switch (numberOfProducts) {
            case 24:
                queryParam = null;
                break;

            case 48:
                queryParam = 'medium';
                break;

            case 72:
                queryParam = 'large';
                break;

            default:
                queryParam = null;
                break;
        }
    }
    return {
        productGridView: queryParam,
    };
};

const getAtgDevIdParam = (action, passAtgDevId, preferences) => {
    const atgDevIdUsrSegValue = get(preferences, 'atgDevIdUsrSeg');
    const hasUsrSegValue =
        atgDevIdUsrSegValue && action.payload.params.UsrSeg === atgDevIdUsrSegValue;
    const isSearchPage = getPageType() === 's';
    const atgProfileId = getCookie('ACCOUNT_ID');
    const visitorId =
        !__SERVER__ && window && window.visitor && window.visitor.getMarketingCloudVisitorID();
    const hasId = !!(atgProfileId || visitorId);
    let atgDevId;

    if (hasUsrSegValue && passAtgDevId && isSearchPage && hasId) {
        if (atgProfileId && visitorId) {
            atgDevId = `atg_${atgProfileId} | dev_${visitorId}`;
        } else if (!atgProfileId && visitorId) {
            atgDevId = `dev_${visitorId}`;
        } else if (atgProfileId && !visitorId) {
            atgDevId = `atg_${atgProfileId}`;
        }

        return {
            atgDevId,
        };
    }

    return atgDevId;
};

/**
 * Update storeIds before making an API call if user is navigating from PDP page and has a userPreferredStore.
 */
function updateStorePDP() {
    const referrerUrl = LocalStorage.getData('galleryReferrer');
    // Comparing referrer URL and current URL to detect user has clicked on back button or not
    const fromPDP =
        referrerUrl &&
        removeQueryStringParameter(getPageURL(true), 'scrollTo') ===
            removeQueryStringParameter(referrerUrl, 'scrollTo');
    if (fromPDP) {
        LocalStorage.removeData('galleryReferrer');
        const userPreferredStore = Cookies.load(LOCATION_SERVICE_COOKIES_USER_PREFERRED_STORE);
        const userStoreDetails = userPreferredStore && JSON.parse(userPreferredStore);
        const storeIds = getURLParameterBykey('storeIds');
        const storeIdsList = (storeIds && storeIds.split('-')) || [];
        if (userStoreDetails && storeIdsList.indexOf(userStoreDetails.storeId) < 0) {
            storeIdsList.push(userStoreDetails.storeId);
            const updatedStoreIds = storeIdsList.join('-');
            const storeParams = {
                storeIds: updatedStoreIds,
                store_availability: 'same day pickup',
            };
            return storeParams;
        }
    }
    return {};
}

/**
 * update storeIds for same day pickup
 */
function updateSameDayPickUpStore(userStore) {
    const storeId = (userStore && userStore.storeId) || '';
    const isSameDayWithoutStoreId =
        storeId &&
        getURLParameterBykey('store_availability') === 'same day pickup' &&
        !getURLParameterBykey('storeIds');
    if (isSameDayWithoutStoreId) {
        return {
            storeIds: storeId,
        };
    }
    return {};
}

function updateDefaultStore(params, userStore) {
    if (!__SERVER__) {
        const updatedParam = {
            ...updateSameDayPickUpStore(userStore),
            ...updateStorePDP(),
        };
        updateUrl(
            getPageURL(),
            {
                ...updatedParam,
            },
            true
        );
        return {
            ...params,
            ...updatedParam,
        };
    }
    return params;
}

function* initiatePendingActions() {
    // These actions are the pending ones after server side rendering
    try {
        yield all([
            put({
                type: SAVEDITEMS_GET_REQUEST,
            }),
        ]);
        enableFilterAnalytics();
    } catch (error) {
        yield put({
            type: GET_GALLERY_DETAILS_BY_ID_ERROR,
            error,
        });
    }
}

/**
 * Unified Constructor API wrapper — handles both search (/s) and browse (/g) pages.
 * Calls the appropriate Constructor endpoint and transforms response to Solr shape.
 * Matches the signature of getGalleryDetailsById so it can be swapped in the race.
 *
 * @param {Object} action - Redux action with payload
 * @param {Object} params - URL/gallery params (searchTerm, categoryId, filters, sort, etc.)
 * @param {Object} storeContext - Store context
 * @param {string} mode - 'search' or 'browse'
 */
function* getConstructorForGallery(action, params, storeContext, mode = 'search') {
    const isBrowse = mode === 'browse';
    try {
        const context = yield select(selectContext);
        const isPreview = context?.isPreview || false;
        const cioConfig = get(context, 'preferences.constructorIO', {}) || {};
        const commonConfig = cioConfig.common || {};
        const searchConfig = cioConfig.search || {};
        // Browse uses browseApiUrl from search config; everything else is shared
        const activeApiUrl = isBrowse
            ? searchConfig.browseApiUrl || 'https://ac.cnstrc.com/browse'
            : searchConfig.searchApiUrl || 'https://ac.cnstrc.com/search';

        // Term (search) or categoryId (browse)
        const searchTerm = isBrowse
            ? ''
            : (params && params.searchTerm) || get(action, 'payload.params.searchTerm', '') || '';

        // For brand pages (/g/brand?brand=X), no id is present in the URL.
        // SOLR handles this via a wildcard category query (q=*) filtered by brand.
        // For Constructor, we use the Browse API with brand as the filter:
        //   /browse/brand/{brandValue} — same as /browse/group_id/{categoryId}
        const isBrandPage =
            !__SERVER__ &&
            window.location.pathname &&
            window.location.pathname.includes('/g/brand');

        // Extract brand param for brand pages
        const brandParam = isBrandPage
            ? (params && params.brand) ||
              (params && params.Brand) ||
              (!__SERVER__ && new URLSearchParams(window.location.search).get('brand')) ||
              ''
            : '';

        const categoryId = isBrowse
            ? (params && params.categoryId) || (params && params.id) || (params && params.N) || ''
            : '';

        // For brand pages without categoryId, use brand as the browse filter
        // effectiveIsBrowse stays true if we have either categoryId OR brand param
        const effectiveIsBrowse = isBrowse && (!!categoryId || (isBrandPage && !!brandParam));
        const effectiveSearchTerm = searchTerm;

        // For browse, if no categoryId and not a valid brand page, return empty
        if (isBrowse && !categoryId && !(isBrandPage && brandParam)) {
            return { data: transformConstructorSearchResponse({}), status: 200, headers: {} };
        }

        /*
         * Derive preview date values when in preview mode.
         * previewDate: ISO string sent to Constructor API to scope results to that date.
         * previewDayKey: DDMmmYYYY string (e.g. "02Oct2026") used to look up product data
         *   under product.data.pf.<previewDayKey> and to match |dayKey suffixes on facet values.
         * Both are only set when isPreview is true
         */
        let previewDate;
        let previewDayKey;
        let previewMinutes;
        if (isPreview) {
            /* previewDate: UTC ISO sent to Constructor as the "now" param (absolute instant).
             * previewDayKey / previewMinutes: Central wall-clock day + minute-of-day used
             *   ONLY for client-side time-level window selection against the markers / pf keys.
             *   These are read straight from the cookie's Central fields (no UTC round-trip),
             *   so the day key does not drift across the UTC boundary.
             */
            previewDate = params?.contentDate
                ? parsePreviewDateToIso(params.contentDate)
                : new Date().toISOString();
            const centralKey = getPreviewCentralKey(params?.contentDate);
            previewDayKey = centralKey.dayKey;
            previewMinutes = centralKey.minutes;
        }

        // Pagination: Constructor supports both page (1-based) and offset (0-based)
        const pageParam = (params && params.page) || 1;
        const page = parseInt(pageParam, 10) || 1;
        const numResultsPerPage = searchConfig.numResultsPerPage || 48;

        // Marketing tiles offset adjustment (search only):
        // When mktTiles are present (from URL param), page 2+ needs offset adjustment
        // so orphan products from page 1 are recovered on page 2.
        let offset;
        if (!effectiveIsBrowse) {
            const mktTiles = parseInt((params && params.mktTiles) || 0, 10) || 0;
            if (page > 1 && mktTiles > 0) {
                offset = (page - 1) * numResultsPerPage - mktTiles;
                if (offset < 0) offset = 0;
            }
        }

        // Sort: gallery passes sort key (e.g. "NA", "BS", "RHL", "LPLH", "LPHL")
        // preview can pass in PLH, PLHS, PHL, PHLS for sorting by price
        // "BM" = Best Match (relevance) and "FS" = Featured (gallery) — both mean no sort for Constructor
        // ascending keys normalize to LPLH; descending keys normalize to LPHL
        const rawSortKey = (params && params.sort) || '';
        let sortKey;
        if (PRICING_SORT_VALUES.PRICE_LOW_HIGH.includes(rawSortKey)) {
            sortKey = SORT_KEYS.PRICE_LOW_HIGH;
        } else if (PRICING_SORT_VALUES.PRICE_HIGH_LOW.includes(rawSortKey)) {
            sortKey = SORT_KEYS.PRICE_HIGH_LOW;
        } else if (
            rawSortKey &&
            rawSortKey !== SORT_KEYS.BEST_MATCH &&
            rawSortKey !== SORT_KEYS.FEATURED
        ) {
            sortKey = rawSortKey;
        }
        const sortBy = sortKey || undefined;
        // eslint-disable-next-line no-nested-ternary
        const sortOrder = sortKey
            ? sortKey === SORT_KEYS.PRICE_LOW_HIGH
                ? 'ascending'
                : 'descending'
            : undefined;

        // Extract facet filters from params:
        const filtersState = yield select((s) => s.filters || {});
        const { filterList = [], storeAvailabilityFilter = [] } = filtersState;
        const knownFacetNames = new Set();
        filterList.forEach((f) => {
            if (f && f.facetInternalName) knownFacetNames.add(f.facetInternalName);
        });
        storeAvailabilityFilter.forEach((f) => {
            if (f && f.facetInternalName) knownFacetNames.add(f.facetInternalName);
        });

        // System params that should never be sent as filters to Constructor.
        const ignoreParams = getConstructorIgnoreParams(context.preferences);

        /* preview cold-load fallback: when a previewDateFacetOptions facet is present in the URL Fetch them up front from Constructor's facet_options endpoint (scoped to the preview `now`)
         */
        const previewFacetFetchEnabled = !!searchConfig.enablePreviewFacetFetch;

        const previewFacetOptionsFallback = {};
        if (
            previewFacetFetchEnabled &&
            isPreview &&
            previewDayKey &&
            params &&
            typeof params === 'object'
        ) {
            const previewDateFacetOptions = searchConfig.previewDateFacetOptions || [];
            const facetsNeedingFetch = previewDateFacetOptions.filter((facetName) => {
                const inUrl = typeof params[facetName] === 'string' && params[facetName];
                const alreadyInState = filterList.some(
                    (f) => f && f.facetInternalName === facetName
                );
                return inUrl && !alreadyInState;
            });
            // Fetch the needed facets in parallel
            const facetOptionsResponses = yield all(
                facetsNeedingFetch.map((facetName) =>
                    call(getConstructorFacetOptions, facetName, {
                        apiUrl: searchConfig.browseApiUrl || 'https://ac.cnstrc.com/browse',
                        indexKey: commonConfig.indexKey || '',
                        accessToken: commonConfig.accessToken || undefined,
                        previewDate,
                        c: 'cio-fe-web-jcpenney',
                    })
                )
            );
            // facet_options response: response.data.response.facets[0].options[]
            facetsNeedingFetch.forEach((facetName, idx) => {
                const facetOptionsResponse = facetOptionsResponses[idx];
                const facetsArr =
                    get(facetOptionsResponse, 'data.response.facets') ||
                    get(facetOptionsResponse, 'response.facets') ||
                    [];
                const matchingFacet = facetsArr.find((facet) => facet && facet.name === facetName);
                previewFacetOptionsFallback[facetName] = (
                    (matchingFacet && matchingFacet.options) ||
                    []
                )
                    .map((opt) => opt && opt.value)
                    .filter((value) => typeof value === 'string' && value);
            });
        }

        const filters = {};
        if (params && typeof params === 'object') {
            Object.keys(params).forEach((key) => {
                if (!params[key] || typeof params[key] !== 'string') return;
                if (key === 'store_availability' || key === 'storeIds') return;
                // Browse: skip categoryId/N/id — they are the group_id, not filters
                if (effectiveIsBrowse && (key === 'categoryId' || key === 'N' || key === 'id'))
                    return;
                // Brand pages: skip brand/Brand — it's the browse filter, not an additional filter
                if (isBrandPage && (key === 'brand' || key === 'Brand')) return;
                if (!ignoreParams.has(key.toLowerCase())) {
                    // Split on pipe (|) only — PLP URLs use pipe (%7C) as the
                    // multi-value facet delimiter (matching SOLR behavior).
                    const values = params[key]
                        .split('|')
                        .map((v) => v.trim())
                        .filter(Boolean);
                    if (values.length > 0) {
                        /* preview: for previewDateFacetOptions facets, the URL/state holds the
                         * STRIPPED value (e.g. "POWER PENNEY DEAL!"). Constructor needs the full
                         * value back. We resolve it from the previous response's facet options
                         * (filterList), which retain the unstripped `originalValue`
                         * ("POWER PENNEY DEAL!|29Sep2026|0000|1259"):
                         *   1. Collect the facet's originalValues for the selected day.
                         *   2. Pick the one whose time window contains the preview time.
                         * Fallback to "label|previewDayKey" (day-level) when no windowed option
                         * matches — covers the legacy day-only feed during rollout.
                         */
                        const previewDateFacetOptions = searchConfig.previewDateFacetOptions || [];
                        const isPreviewHiddenFacet =
                            previewDayKey && previewDateFacetOptions.includes(key);
                        /* For each selected stripped label, send EVERY window value whose range
                         * contains the preview time — not just one.
                         * all of them returns the full union of products on that promo at that
                         * minute. Fall back to day-level ("label|previewDayKey") when no window  value matches (cold load with no options, or day-only feed). */
                        const constructorValues = isPreviewHiddenFacet
                            ? values.flatMap((value) => {
                                  const facetEntry = filterList.find(
                                      (filter) => filter && filter.facetInternalName === key
                                  );
                                  const facetOpts = (facetEntry && facetEntry.facetValueList) || [];
                                  // set (all overlapping windows) in activeOriginalValues
                                  const matchingOpt = facetOpts.find(
                                      (opt) =>
                                          opt && stripPreviewSuffix(opt.originalValue || '') === value
                                  );
                                  if (
                                      matchingOpt &&
                                      Array.isArray(matchingOpt.activeOriginalValues) &&
                                      matchingOpt.activeOriginalValues.length > 0
                                  ) {
                                      return matchingOpt.activeOriginalValues;
                                  }
                                  const stateOriginalValues = facetOpts
                                      .map((opt) => opt && opt.originalValue)
                                      .filter(Boolean);
                                  const sourceOriginalValues =
                                      stateOriginalValues.length > 0
                                          ? stateOriginalValues
                                          : previewFacetOptionsFallback[key] || [];
                                  const candidates = sourceOriginalValues.filter(
                                      (originalValue) =>
                                          typeof originalValue === 'string' &&
                                          stripPreviewSuffix(originalValue) === value
                                  );
                                  const activeValues = selectActivePreviewWindows(
                                      candidates,
                                      previewDayKey,
                                      previewMinutes
                                  );
                                  return activeValues.length > 0
                                      ? activeValues
                                      : [`${value}|${previewDayKey}`];
                              })
                            : values;
                        filters[`filters[${key}]`] = isPreviewHiddenFacet
                            ? [...new Set(constructorValues)]
                            : constructorValues;
                    }
                }
            });
        }

        applyRangeFilters(params, searchConfig, filters);

        // Map storeIds (URL param) → store_ids (Constructor facet name)
        if (params && params.storeIds && typeof params.storeIds === 'string') {
            const storeIdValues = params.storeIds.split('-').filter(Boolean);
            if (storeIdValues.length > 0) {
                filters['filters[store_ids]'] = storeIdValues;
            }
        }

        // Map store_availability "same day pickup" → Constructor facet filter
        if (params && params.store_availability) {
            const storeAvailValue = params.store_availability.replace(/\+/g, ' ');
            if (
                storeAvailValue.toLowerCase() === 'same day pickup' ||
                storeAvailValue.toLowerCase() === 'both'
            ) {
                filters['filters[store_availability]'] = ['same day pickup'];
            }
        }

        // Apply configurable default filters (e.g., Network_Status:1 for in-stock only)
        const defaultFilters = searchConfig.defaultFilters || {};
        Object.keys(defaultFilters).forEach((filterName) => {
            const filterValue = defaultFilters[filterName];
            if (filterValue !== undefined && filterValue !== null && filterValue !== '') {
                filters[`filters[${filterName}]`] = [String(filterValue)];
            }
        });

        // set default item_status on initial load if not already selected
        const { enableDefaultFilterForPreview = false } = get(context, 'featureFlags', {});
        if (isPreview && enableDefaultFilterForPreview && !filters['filters[item_status]']) {
            const isPagination = !action.payload.initialLoad && !!params?.page;
            const itemStatusInSelected =
                isPagination &&
                (yield select((s) => s.selectedFilters || [])).some(
                    (f) => f.facetInternalName === 'item_status'
                );
            if (action.payload.initialLoad || itemStatusInSelected) {
                filters['filters[item_status]'] = ['1'];
            }
        }

        const config = {
            apiUrl: activeApiUrl,
            indexKey: commonConfig.indexKey || '',
            accessToken: commonConfig.accessToken || undefined,
            numResultsPerPage,
            variationsMap: searchConfig.variationsMap || undefined,
            /* Preview: un-hide the pf (previewable fields) data for the selected day so the
             * transformer can read it. The request is always `pf.<dayKey>` — the day remains the
             * top-level pf key. Time-level windows are nested INSIDE that */
            fmtOptions: previewDayKey
                ? {
                      ...(searchConfig.fmtOptions || {}),
                      hidden_fields: [`${PREVIEWABLE_FIELDS_KEY}.${previewDayKey}`],
                  }
                : searchConfig.fmtOptions || undefined,
            page: offset !== undefined && offset !== null ? undefined : page,
            offset,
            sortBy,
            sortOrder,
            filters: Object.keys(filters).length > 0 ? filters : undefined,
            c: 'cio-fe-web-jcpenney',
            i: getCookie('ConstructorioID_client_id') || undefined,
            s: getCookie('ConstructorioID_session_id') || undefined,
            ui: (() => {
                if (__SERVER__) return undefined;
                const userState = LocalStorage.getData('DP_USER_STATE');
                if (userState && userState !== '0') {
                    return LocalStorage.getData('ACCOUNT_ID') || undefined;
                }
                return undefined;
            })(),
            // us (user segments) — append boostUS value from URL if present
            // e.g. boostUS=SP-_-HOME-_-TB-_-SJB-FLASH-SALE-_-SJB-FLASH-SALE → us=SP-_-HOME-_-TB-_-SJB-FLASH-SALE-_-SJB-FLASH-SALE
            us: (() => {
                if (__SERVER__) return undefined;
                const boostUS = (params && params.boostUS) || getURLParameterBykey('boostUS') || '';
                if (!boostUS) return undefined;
                // boostUS may be URL-encoded — decode it
                try {
                    return [decodeURIComponent(boostUS.replace(/\+/g, ' '))];
                } catch (e) {
                    return [boostUS];
                }
            })(),
            ...(previewDate ? { previewDate } : {}),
        };

        // Zone/Localization — configurable via preferences.constructorIO.zone
        const zoneConfig = get(context, 'preferences.constructorIO.zone', {}) || {};
        const geoZip = params && params.geoZip;
        if (zoneConfig.enabled && geoZip) {
            try {
                let zoneValue = '';
                const cachedZoneStr = !__SERVER__ && sessionStorage.getItem('cio_zone');
                const cachedZone = cachedZoneStr ? JSON.parse(cachedZoneStr) : null;

                if (cachedZone && cachedZone.zipcode === geoZip && cachedZone.zone) {
                    zoneValue = cachedZone.zone;
                } else {
                    const zoneTimeout = zoneConfig.timeout || 3000;
                    const zoneApiUrl = zoneConfig.zoneApiUrl || '';
                    if (zoneApiUrl) {
                        const { zoneResponse, zoneTimedOut } = yield race({
                            zoneResponse: call(getConstructorZone, zoneApiUrl, geoZip),
                            zoneTimedOut: delay(zoneTimeout),
                        });
                        if (!zoneTimedOut && zoneResponse) {
                            zoneValue =
                                get(zoneResponse, 'data.zoneInfo.zone', '') ||
                                get(zoneResponse, 'data.zone', '');
                            if (zoneValue && !__SERVER__) {
                                sessionStorage.setItem(
                                    'cio_zone',
                                    JSON.stringify({ zipcode: geoZip, zone: zoneValue })
                                );
                            }
                        }
                        if (zoneTimedOut) {
                            yield put(
                                triggerFormError([
                                    {
                                        errorDescription: `constructor ${mode} - zone api timeout - zipcode: ${geoZip} - timeout: ${zoneTimeout}ms`,
                                    },
                                ])
                            );
                        }
                    }
                }

                if (zoneValue && zoneConfig.template) {
                    const templateStr =
                        typeof zoneConfig.template === 'string'
                            ? zoneConfig.template
                            : JSON.stringify(zoneConfig.template);
                    config.qs = templateStr.replace('{{zone}}', zoneValue);
                } else if (zoneValue) {
                    // Static qs is the base — set it before merging localization
                    if (!config.qs && searchConfig.staticQs) {
                        const { staticQs } = searchConfig;
                        config.qs =
                            typeof staticQs === 'string' ? staticQs : JSON.stringify(staticQs);
                    }
                    const qsObj = config.qs ? JSON.parse(config.qs) : {};
                    const localizationCondition = {
                        or: [
                            { name: 'is_sku_localized', value: false },
                            {
                                and: [
                                    { name: 'is_sku_localized', value: true },
                                    { name: 'fulfilment_zones', value: zoneValue },
                                ],
                            },
                        ],
                    };
                    if (qsObj.pre_filter_expression) {
                        if (qsObj.pre_filter_expression.and) {
                            qsObj.pre_filter_expression.and.push(localizationCondition);
                        } else {
                            qsObj.pre_filter_expression = {
                                and: [qsObj.pre_filter_expression, localizationCondition],
                            };
                        }
                    } else {
                        qsObj.pre_filter_expression = { and: [localizationCondition] };
                    }
                    config.qs = JSON.stringify(qsObj);
                }
            } catch (zoneErr) {
                logger.error({
                    errorMessage: `Zone API call failed for Constructor ${mode}`,
                    exception: zoneErr,
                });
            }
        }

        // Static qs fallback — only runs if zone was not active
        if (!config.qs && searchConfig.staticQs) {
            const { staticQs } = searchConfig;
            config.qs = typeof staticQs === 'string' ? staticQs : JSON.stringify(staticQs);
        }

        // pp_is_ensemble filter — exclude ensemble products (configurable per page type)
        // Ensemble products (pp_is_ensemble: "Y") can be excluded from search and/or browse
        // Config: ensembleFilter: { searchEnabled: true, browseEnabled: false }
        // Exception: When searching for ensemble IDs (terms starting with "enr"), skip the filter
        const ensembleFilterConfig = searchConfig.ensembleFilter || {};
        const isEnsembleIdSearch =
            searchTerm &&
            typeof searchTerm === 'string' &&
            searchTerm.toLowerCase().startsWith('enr');
        const applyEnsembleFilter = isEnsembleIdSearch
            ? false // Skip ensemble filter for ensemble ID searches (e.g., enr5008807713)
            : isBrowse
            ? ensembleFilterConfig.browseEnabled === true // browse: disabled by default
            : ensembleFilterConfig.searchEnabled !== false; // search: enabled by default

        if (applyEnsembleFilter) {
            try {
                const qsObj = config.qs ? JSON.parse(config.qs) : {};
                // Exclude ensemble products (entity_type: "ENSEMBLE")
                const ensembleCondition = { not: { name: 'entity_type', value: 'ENSEMBLE' } };

                if (qsObj.pre_filter_expression) {
                    // Merge with existing pre_filter_expression
                    if (qsObj.pre_filter_expression.and) {
                        qsObj.pre_filter_expression.and.push(ensembleCondition);
                    } else {
                        qsObj.pre_filter_expression = {
                            and: [qsObj.pre_filter_expression, ensembleCondition],
                        };
                    }
                } else {
                    // Create new pre_filter_expression
                    qsObj.pre_filter_expression = ensembleCondition;
                }
                config.qs = JSON.stringify(qsObj);
            } catch (ensembleErr) {
                // If parsing fails, leave qs unchanged
            }
        }

        // variations_map filter_by — mirrors active facet filters + zone into variations_map
        const variationsMapFilterByConfig = searchConfig.variationsMapFilterBy || {};
        if (variationsMapFilterByConfig.enabled !== false && config.variationsMap) {
            const allowedFilterByFacets = new Set(variationsMapFilterByConfig.facets || ['size']);
            const filterByConditions = [];

            if (variationsMapFilterByConfig.networkStatusEnabled !== false) {
                filterByConditions.push({ field: 'data.Network_Status', value: 1 });
            }

            if (variationsMapFilterByConfig.localizationEnabled !== false) {
                const resolvedZone = (() => {
                    try {
                        const cached = !__SERVER__ && sessionStorage.getItem('cio_zone');
                        return cached ? JSON.parse(cached).zone || '' : '';
                    } catch (e) {
                        return '';
                    }
                })();
                if (resolvedZone) {
                    filterByConditions.push({
                        or: [
                            { field: 'data.is_sku_localized', value: false },
                            {
                                and: [
                                    { field: 'data.is_sku_localized', value: true },
                                    { field: 'data.fulfilment_zones', value: resolvedZone },
                                ],
                            },
                        ],
                    });
                }
            }

            if (
                variationsMapFilterByConfig.facetsEnabled !== false &&
                Object.keys(filters).length > 0
            ) {
                Object.keys(filters).forEach((filterKey) => {
                    const match = filterKey.match(/^filters\[(.+)\]$/);
                    if (!match) return;
                    const facetName = match[1];
                    if (!allowedFilterByFacets.has(facetName)) return;
                    const values = filters[filterKey];
                    if (!values || !Array.isArray(values) || values.length === 0) return;

                    if (values.length === 1) {
                        filterByConditions.push({ field: `data.${facetName}`, value: values[0] });
                    } else {
                        filterByConditions.push({
                            or: values.map((v) => ({ field: `data.${facetName}`, value: v })),
                        });
                    }
                });
            }

            if (filterByConditions.length > 0) {
                const variationsMapWithFilter = JSON.parse(JSON.stringify(config.variationsMap));
                if (filterByConditions.length === 1) {
                    variationsMapWithFilter.filter_by = filterByConditions[0];
                } else {
                    variationsMapWithFilter.filter_by = { and: filterByConditions };
                }
                config.variationsMap = variationsMapWithFilter;
            }
        }

        // Constructor API call with timeout
        const constructorTimeout = searchConfig.timeout || 10000;
        const apiCallStart = Date.now();

        // For brand pages: use browse API with brand filter (/browse/brand/{brandValue})
        // For regular browse: use group_id (/browse/group_id/{categoryId})
        // For search: use search term
        let term;
        let browseFilterName;
        if (effectiveIsBrowse) {
            if (categoryId) {
                term = categoryId;
                browseFilterName = 'group_id';
            } else if (isBrandPage && brandParam) {
                term = brandParam;
                browseFilterName = 'brand'; // or 'Brand' depending on your Constructor index
            }
        } else {
            term = effectiveSearchTerm;
        }

        const apiFunction = effectiveIsBrowse
            ? getConstructorBrowseResults
            : getConstructorSearchResults;

        // Pass browseFilterName to config for browse API calls
        const apiConfig =
            effectiveIsBrowse && browseFilterName !== 'group_id'
                ? { ...config, browseFilterName }
                : config;

        const { response: apiResponse, timedOut } = yield race({
            response: call(apiFunction, term, apiConfig),
            timedOut: delay(constructorTimeout),
        });

        if (timedOut) {
            yield put(
                triggerFormError([
                    {
                        errorDescription: `constructor ${mode} - api call timeout error - ${
                            effectiveIsBrowse ? 'catId' : 'term'
                        }: ${term} - timeout: ${constructorTimeout}ms`,
                    },
                ])
            );
            return { data: transformConstructorSearchResponse({}), status: 200, headers: {} };
        }

        const apiDuration = Date.now() - apiCallStart;
        const response = apiResponse;
        const cioData = (response && response.data) || {};

        // Measure transformation layer time
        const transformStart = Date.now();
        const transformedData = transformConstructorSearchResponse(cioData, {
            page,
            params,
            isPreview,
            previewDayKey,
            previewMinutes,
            previewDateFacetOptions: searchConfig.previewDateFacetOptions || [],
            appliedFilters: filters,
            pageType: effectiveIsBrowse ? 'g' : 's',
            categoryId: effectiveIsBrowse ? categoryId : undefined,
            galleryPath:
                effectiveIsBrowse && !__SERVER__
                    ? window.location.pathname.replace(/^\//, '')
                    : undefined,
            maxPaginationPages: searchConfig.maxPaginationPages || 25,
            disableDeepPagination: searchConfig.disableDeepPagination !== false,
            templateTypesForMiniPdp: searchConfig.templateTypesForMiniPdp || undefined,
            pdpEnabledFacets: searchConfig.pdpEnabledFacets || undefined,
            pdpFacetGroupMap: searchConfig.pdpFacetGroupMap || undefined,
            maxFacetCategories: searchConfig.maxFacetCategories || 12,
            enableRelatedSearchTransform: commonConfig.enableRelatedSearchTransform !== false,
            hideFacetsOnSearch: searchConfig.hideFacetsOnSearch || ['s1_discount_range'],
            hideFacetsOnBrowse: searchConfig.hideFacetsOnBrowse || [],
            groupImageCategories: searchConfig.groupImageCategories || [
                'cat100250084',
                'cat100250068',
                'cat100250069',
            ],
        });
        const transformDuration = Date.now() - transformStart;
        console.log(
            `[ConstructorIO ${
                effectiveIsBrowse ? 'Browse' : 'Search'
            }] API: ${apiDuration}ms | Transform: ${transformDuration}ms${
                effectiveIsBrowse ? ` | catId: ${categoryId}` : ''
            }`
        );

        // WebId/LotId/Ensemble ID detection — search only (browse doesn't need it)
        if (!effectiveIsBrowse && transformedData) {
            const { enableSearchByWebIdLotIdEnsembleID = true } = yield select(getFeatureFlags);
            if (enableSearchByWebIdLotIdEnsembleID) {
                const productIdInfo = detectProductId(
                    searchTerm,
                    enableSearchByWebIdLotIdEnsembleID
                );
                const totalResults =
                    (transformedData.organicZoneInfo &&
                        transformedData.organicZoneInfo.totalNumRecs) ||
                    0;

                if (
                    productIdInfo.isProductId &&
                    totalResults === 0 &&
                    productIdInfo.type === 'webId'
                ) {
                    try {
                        const aliasResponse = yield race({
                            response: call(
                                getProductAlias,
                                stripSpaces(productIdInfo.sanitizedTerm)
                            ),
                            timeout: delay(5000),
                        });
                        const pdpUrl = aliasResponse?.response?.data?.pdpUrl || '';
                        if (pdpUrl) {
                            transformedData.pdPageURL = pdpUrl;
                        }
                    } catch (e) {
                        logger.error({
                            errorMessage: 'Browse-service product-aliases call failed',
                            exception: e,
                        });
                    }
                }
            }
        }

        // Redirect message handling — search only
        if (!effectiveIsBrowse && params && params.addmsg === 'true' && transformedData) {
            const storedMessage = LocalStorage.getData('constructorRedirectMessage');
            if (storedMessage) {
                transformedData.keywordRedirectMessage = storedMessage;
                LocalStorage.removeData('constructorRedirectMessage');
            }
        }

        return {
            data: transformedData || {},
            status: 200,
            headers: {},
        };
    } catch (error) {
        logger.error({
            errorMessage: `Constructor ${
                effectiveIsBrowse ? 'Browse' : 'Search'
            } API call failed, returning empty response`,
            exception: error,
        });
        return {
            data: transformConstructorSearchResponse({}),
            status: 200,
            headers: {},
        };
    }
}

function* formatPayload() {
    const context = yield select(selectContext);
    const preferences = context.preferences || {};
    const abTestMboxName = get(preferences, 'abTest.mbox', 'yoda-userseg-mbox');
    const cleansedPayload = {
        abInput: {
            mbox: abTestMboxName,
        },
    };

    return cleansedPayload;
}

function* getABTestEngagementsSaga(apiPayload) {
    let usersegval = '';
    try {
        if (apiPayload) {
            const updatedapiPayload = yield call(formatPayload);
            let abTestEngagementsResponse = yield call(getABTestEngagements, updatedapiPayload);
            if (abTestEngagementsResponse && abTestEngagementsResponse.data) {
                abTestEngagementsResponse = get(abTestEngagementsResponse.data, 'testVersion', '');
                yield put({
                    type: AB_TEST_DETAILS_SUCCESS,
                    abTestEngagementsResponse,
                });
                const values =
                    abTestEngagementsResponse &&
                    abTestEngagementsResponse.indexOf('UsrSeg') !== -1 &&
                    abTestEngagementsResponse.split('=');
                usersegval = values && values.length > 1 ? values[1].trim() : ''; // get UsrSeg value from abTestEngagementsResponse
                const abTestPayload = {
                    UsrSeg: usersegval,
                };
                return abTestPayload;
            }
        }
    } catch (error) {
        yield put({
            type: 'AB_TEST_ENGAGEMENTS_ERROR',
            error,
        });
    }
    return {};
}

function* galleryScrollHandler() {
    const state = yield select((state) => state);
    const interactiveVizNav = getInteractiveVizNavFromIrisDataState(state);
    const breadCrumbInfo = getBreadCrumbInfo(state);
    const deviceType = get(state, 'context.deviceType', {});
    const hideBreadCrumbsWhenNoNavigation = selectHideBreadCrumbsWhenNoNavigation(state);
    const { filterName = '' } = getLastFilter() || {};
    const isNative = get(state, 'context.isNative', false);
    const paginationClickBack = LocalStorage.getData('paginationClickBack');
    const faceteventtriggered = LocalStorage.getData('faceteventtriggered');

    if (faceteventtriggered || filterName === 'same day pickup' || !isEmpty(interactiveVizNav)) {
        scrollTitleIntoView('#gallery-product-list', deviceType?.isDesktop, isNative);
        LocalStorage.removeData('faceteventtriggered');
    } else if (document.getElementById('inspirational-block')) {
        scrollTitleIntoView('#inspirational-block', deviceType?.isDesktop, isNative);
    } else if (paginationClickBack) {
        scrollTitleIntoView('#gallery-pagination', deviceType?.isDesktop, isNative, true);
        LocalStorage.removeData('paginationClickBack');
        LocalStorage.removeData('browserBFButtonClick');
    } else if (
        !isNative &&
        hasBreadCrumbLinks(breadCrumbInfo, deviceType, hideBreadCrumbsWhenNoNavigation)
    ) {
        scrollTitleIntoView(
            '[data-automation-id="product-breadcrumbs"',
            deviceType?.isDesktop,
            isNative
        );
    } else {
        scrollTitleIntoView('#gallery-title', deviceType?.isDesktop, isNative);
    }
}

function* galleryDetailsSaga(action) {
    try {
        yield all([
            put({
                type: RESET_COMMON_DATA,
                initialLoad: action.payload.initialLoad,
            }),
            // Disabling as part of TOFP2P-757
            // put({ type: SHOW_LOADER_GALLERY }),
            put({
                type: REFRESH_CONTENT,
                refreshContent: false,
            }),
        ]);
        const { passAtgDevId } = yield select(getFeatureFlags);
        const storeContext = yield select(getStoreContext);
        const productsList = yield select(getStoreProducts);
        // eslint-disable-next-line no-unused-vars
        const isInitialIrisCallClient = yield select(getIsInitialCallClient);

        // get preferences data
        const context = yield select(selectContext);
        const preferences = context.preferences || {};
        const {
            enableXGNSCanonicalRedirect: enableXGNSRedirect,
            disableOrphanProducts,
            enableVisualRelatedSearch,
            enableDynamicVisNav,
            enableSSRForPLP,
            enableBloomReach = false,
            enableBloomReachV3PLP = true,
            enableBloomReachNative = false,
            enableBrStoreNullResultsMessage = true,
            enableFindaStoreNullResults = true,
            enableCriteo = false,
            enableSponsoredProduct = false,
            enableSponsoredProductsOverflow = false,
            enableSponsoredProductNative = true,
            enableMultipleThumbnails = false,
        } = yield select(getFeatureFlags);
        const isNative = context.isNative || false;
        const isNativeBloomReach = enableBloomReachNative ? false : isNative;
        const isbloomReachAudience =
            isBloomReachAudience(enableBloomReach, isNativeBloomReach) && getPageType() === 's';
        const { requestUrl = '' } = context;
        const {
            plpBloomreachInclusionList = [],
            sponsoredProductsInclusionList = [],
            sponsoredProductsEnableFilters = [],
        } = preferences;
        const isBloomReachV3Page = enableBloomReachPage(
            enableBloomReachV3PLP,
            requestUrl,
            plpBloomreachInclusionList
        );

        // BR use store in url to set store when landing on no results page. handle back button with store changes
        const fromNoResultsPage = !!LocalStorage.getData('NoResultsPage');
        const { storeIds } = action.payload.params;
        const userStore = yield select(getBOPISStore);
        const BrNoResultsWithStoreId = !!(
            fromNoResultsPage &&
            (isBloomReachV3Page || isbloomReachAudience) &&
            storeIds &&
            storeIds !== userStore?.storeId
        );

        const storesIdFromUrl = getURLParameterBykey('storeIds');

        if (BrNoResultsWithStoreId) {
            yield put(
                updateSelectedStoreLocationInfo({
                    selectedStoreId: storeIds,
                })
            );
        }

        const apiParams =
            action.payload.initialLoad && !BrNoResultsWithStoreId
                ? updateDefaultStore(action.payload.params, userStore)
                : action.payload.params;

        const isAdTrackingEnabled = isCookieEnabled(TRACKING, OPTANON_COOKIE);

        // TOFP2P-3676 criteo call changed to be here so we get storeId appended to params if they are sent to SOLR as well
        if (enableCriteo && isAdTrackingEnabled) {
            const enableFilters = sponsoredProductsEnableFilters?.includes(action.payload.pageType);
            const sortClicked = enableFilters
                ? !action.payload.initialLoad && LocalStorage.getData('sortClicked')
                : true;

            const paginationClick =
                !action.payload.initialLoad &&
                (LocalStorage.getData('paginationClick') ||
                    LocalStorage.getData('paginationClickBack'));

            if (action.payload.initialLoad || paginationClick || !sortClicked) {
                LocalStorage.removeData('paginationClick');

                // new code to set id for brand pages with id
                let criteoParams = apiParams;
                if (
                    action.payload.pageType === 'Gallery' &&
                    !criteoParams?.id &&
                    action.payload?.apiPath === 'g/brand'
                ) {
                    criteoParams = {
                        ...apiParams,
                        id: allProductsCategory,
                    };
                }

                const isNativeSponsoredProduct =
                    isNative && enableSponsoredProductNative ? true : !isNative;

                const isSponsoredProductEnabled =
                    enableSponsoredProduct &&
                    sponsoredProductsInclusionList.includes(action.payload.pageType) &&
                    !action.payload.params?.page &&
                    isNativeSponsoredProduct;

                yield put({
                    type: SPONSORED_DISPLAY_PAGE,
                    sponsoredDisplayPage: isSponsoredProductEnabled,
                });

                yield put(
                    getCriteoData({
                        pageType: action.payload.pageType,
                        params: criteoParams,
                        waitForCriteo: isSponsoredProductEnabled,
                    })
                );
            } else {
                LocalStorage.removeData('sortClicked');
                yield put(clearCriteoData());
                yield put({
                    type: SPONSORED_DISPLAY_PAGE,
                    sponsoredDisplayPage: false,
                });
            }
        }
        // Failing on Server Side Will re-initiate the call on client side.
        const timeout = storeContext.isPreview ? 45000 : __SERVER__ ? 50000 : 20000; //eslint-disable-line

        // Get the UsrSeq value and send it to Solr for AB Testing.
        let galleryparams = action.payload.params || {};

        delete action.payload.params.atgdevid; // eslint-disable-line no-param-reassign
        delete apiParams.atgdevid;

        const { sponsoredProductSlotConfig } = preferences;
        const { enableFourThumbnailsGrid = false, enableLocalization = false } = yield select(
            getFeatureFlags
        );

        const isLocalized =
            enableLocalization && (!galleryparams?.store_availability || !galleryparams?.storeIds);

        if (!isLocalized) {
            // prevent geoZip from persisting due to url params being read
            delete galleryparams?.geoZip;
            delete apiParams?.geoZip;
            updateUrl(
                getPageURL(),
                {
                    geoZip: null,
                },
                true
            );
        }

        // Constructor Integration — Search (/s/) and Browse (/g/) pages
        const featureFlags = yield select(getFeatureFlags);
        const currentPageType = getPageType(); // 's' = search, 'g' = gallery/PLP
        const isSearchPage = currentPageType === 's';
        const isGalleryPage = currentPageType === 'g';
        let isConstructorSearchEnabled = false;
        let isConstructorBrowseEnabled = false;
        try {
            const cioConfig = get(preferences, 'constructorIO', {}) || {};
            const commonConfig = cioConfig.common || {};
            const enabledPageTypes = commonConfig.pageTypes || ['s']; // default: search only
            if (
                enabledPageTypes.includes(currentPageType) &&
                isConstructorEnabled(featureFlags || {}, preferences || {})
            ) {
                isConstructorSearchEnabled = isSearchPage;
                isConstructorBrowseEnabled = isGalleryPage;
            }
        } catch (e) {
            // If isConstructorEnabled throws for any reason, fall back to SOLR
            isConstructorSearchEnabled = false;
            isConstructorBrowseEnabled = false;
        }

        const useConstructor = isConstructorSearchEnabled || isConstructorBrowseEnabled;

        // Determine which Constructor mode to use
        const constructorMode = isConstructorBrowseEnabled ? 'browse' : 'search';

        const apiCall = yield race({
            galleryDetails: useConstructor
                ? call(
                      getConstructorForGallery,
                      action,
                      {
                          ...productsParam(getProductsPerPage(storeContext.preferences)),
                          ...action.payload.params,
                          ...galleryparams,
                          ...apiParams,
                          responseType: 'organic',
                          ...getAtgDevIdParam(action, passAtgDevId, preferences),
                          ...(isLocalized && {
                              geoZip: getLocalizationZipcode(),
                          }),
                      },
                      storeContext,
                      constructorMode
                  )
                : call(
                      getGalleryDetailsById,
                      action,
                      {
                          ...productsParam(getProductsPerPage(storeContext.preferences)),
                          ...action.payload.params,
                          ...galleryparams,
                          ...apiParams,
                          responseType: 'organic',
                          ...getAtgDevIdParam(action, passAtgDevId, preferences),
                          ...(isLocalized && {
                              geoZip: getLocalizationZipcode(),
                          }),
                      },
                      storeContext
                  ),
            timeout: delay(timeout),
        });

        const galleryDetails = apiCall?.galleryDetails;

        if (enableMultipleThumbnails && galleryDetails?.data?.organicZoneInfo?.products) {
            const splitPPIds = getMultipleThumbnailsPpIds(
                galleryDetails.data.organicZoneInfo.products
            );
            galleryDetails.data.hasMultipleThumbnails =
                splitPPIds !== 'n/a' && splitPPIds.length > 0;
            galleryDetails.data.splitPPIds = splitPPIds;
        } else {
            galleryDetails.data.hasMultipleThumbnails = false;
        }

        // Debugger for Serverside data.
        if (galleryDetails) {
            const actions = [];
            if (
                enableBrStoreNullResultsMessage &&
                (isBloomReachV3Page || isbloomReachAudience) &&
                storesIdFromUrl
            ) {
                const galleryData = get(galleryDetails, 'data.organicZoneInfo.products', []);
                const productsList = yield select(getStoreProducts);
                if (
                    (galleryData.length === 0 && productsList.length !== 0) ||
                    (enableFindaStoreNullResults &&
                        galleryData.length === 0 &&
                        productsList.length === 0)
                ) {
                    const removeStoreParams = (url) => {
                        const ignoredParams = ['storeIds', 'store_availability', 'activeFacetId'];
                        let updatedUrl = url;

                        ignoredParams.forEach((ignoredParam) => {
                            updatedUrl = removeQueryStringParameter(updatedUrl, ignoredParam);
                        });

                        return updatedUrl;
                    };

                    const removeStoreID = removeStoreParams(location.pathname + location.search);
                    LocalStorage.setData('galleryNoResults', true);

                    history.pushState({}, '', removeStoreID);
                    window.location.reload();
                    return;
                }
            }
            if (__SERVER__ && enableSSRForPLP) {
                const { headers: { _headers: headers = {} } = {} } = galleryDetails;
                const responseControl = headers['cache-control'];
                if (responseControl) {
                    const [cacheControl] = responseControl;
                    const [ttl] = cacheControl.split('=');
                    if (ttl) {
                        yield put({
                            type: 'SET_SOLR_CACHE',
                            cache: {
                                ttl,
                            },
                        });
                    }
                }
            }
            // Store Constructor redirect message in localStorage before redirect,
            // so the target page can display it via GalleryTitle component.
            const constructorRedirectMsg = get(galleryDetails, 'data.keywordRedirectMessage', '');
            if (constructorRedirectMsg && isConstructorSearchEnabled) {
                LocalStorage.setData('constructorRedirectMessage', constructorRedirectMsg);
            }
            const redirect = redirectRequired(
                galleryDetails.data,
                action.payload.loc.pathname,
                enableXGNSRedirect
            );
            const galleryData = get(galleryDetails, 'data', {});
            const gallerySearchStatus = get(galleryDetails, 'status', 200);
            if (__SERVER__ && redirect) {
                yield put({
                    type: 'SET_REDIRECT_URL',
                    redirectData: redirect,
                });
            }

            actions.push(
                put({
                    type: HIDE_LOADER_GALLERY,
                })
            );
            // Invoke gallery actions only if redirect is false
            if (!redirect) {
                if (productsList.length) {
                    actions.push(
                        put({
                            type: RERENDER_CERTONA,
                            payload: {
                                reRenderCertona: true,
                            },
                        })
                    );
                }
                // https://jira.jcpenney.com/browse/MNPDPYODA-9656 IRIS migration | H 1 scroll to gallery title after pagination is not working
                if (!action.payload.initialLoad) {
                    actions.push(
                        put({
                            type: RERENDER_COMPONENT,
                            payload: true,
                        })
                    );
                }
                if (gallerySearchStatus === 403) {
                    // refatcor needed need to work without passing gallerydetail obj
                    actions.push(
                        put({
                            type: GET_GALLERY_DETAILS_BY_ID_SUCCESS,
                            galleryStatus: gallerySearchStatus,
                            galleryDetails: {},
                            deviceType: storeContext.deviceType,
                        })
                    );
                } else {
                    actions.push(
                        put({
                            type: GET_GALLERY_DETAILS_BY_ID_SUCCESS,
                            galleryDetails: galleryData,
                            deviceType: storeContext.deviceType,
                            productsPerPage: getProductsPerPage(storeContext.preferences),
                        })
                    );

                    // TOFP2P-3524 - Changes to fix TOFP2P-3425 caused major lag so we are ok with reverting back.
                    const backBtnClick = LocalStorage.getData('browserBFButtonClick');
                    const paginationClickBack = !!LocalStorage.getData('paginationClickBack');
                    if (!action.payload.initialLoad && (!backBtnClick || paginationClickBack)) {
                        yield call(galleryScrollHandler);
                    } else {
                        LocalStorage.removeData('galleryMarketingScrollTo');
                        LocalStorage.removeData('fromGallery');
                        LocalStorage.removeData('browserBFButtonClick');
                        LocalStorage.removeData('NoResultsPage');
                    }
                    actions.push(
                        put({
                            type: LAST_GALLERY_ACTION_RESULT,
                            galleryDetails: galleryData,
                            lastAction: action,
                        })
                    );

                    if (action.payload.AMPpage || __SERVER__) {
                        actions.push(
                            put({
                                type: GET_PRODUCT_LIST_SUCCESS,
                                galleryDetails: galleryData,
                                deviceType: storeContext.deviceType,
                                productsPerPage: getProductsPerPage(storeContext.preferences),
                                disableOrphanProducts,
                                enableVisualRelatedSearch,
                                enableDynamicVisNav,
                                pageType: getPageType(),
                            })
                        );
                        actions.push(
                            put({
                                type: GET_FILTERS_SUCCESS,
                                galleryDetails: galleryData,
                                isbloomReachAudience,
                                isBloomReachV3Page,
                                isConstructorSearchEnabled: useConstructor,
                            })
                        );
                    } else if (!__SERVER__) {
                        // expose sku swatches globally for A/B Testing
                        exposeSkuSwatches(galleryData);
                        // expose ensemble products info for A/B Testing
                        exposeEnsembleData(galleryData);

                        let criteo = yield select(getCriteo);
                        let sponsoredgalleryData = criteo?.sponsoredProduct?.data;
                        const waitForCriteo = criteo?.waitForCriteo;
                        if (enableSponsoredProduct && waitForCriteo) {
                            const sponsoredProductTimeout =
                                storeContext?.preferences?.sponsoredProductTimeout?.[
                                    action?.payload?.pageType
                                ] || 15000; // 15 seconds timeout
                            const { success, timeout } = yield race({
                                success: take('SPONSORED_GET_RESULTS'),
                                timeout: delay(sponsoredProductTimeout), // 5 seconds timeout
                            });

                            criteo = yield select(getCriteo);
                            sponsoredgalleryData = success ? criteo?.sponsoredProduct?.data : {};
                            if (timeout) {
                                yield put(
                                    triggerFormError([
                                        {
                                            errorDescription: `criteo ad - aggregator call timeout error`,
                                        },
                                    ])
                                );
                            }
                        }
                        if (
                            enableSponsoredProduct &&
                            sponsoredgalleryData?.OOSProducts?.length > 0
                        ) {
                            const OOSProducts = sponsoredgalleryData.OOSProducts.filter(
                                (product) => product?.ppActive && !product?.inStock
                            ).map((item) => ({
                                errorDescription: `criteo ad - browse api error - PPID:${item?.ParentSKU}:out of stock`,
                            }));

                            if (OOSProducts.length > 0) {
                                yield put(triggerOOSSponsoredProductFormError(OOSProducts));
                            }
                        }

                        actions.push(
                            put({
                                type: GET_PRODUCT_LIST_SUCCESS,
                                galleryDetails: galleryData,
                                deviceType: storeContext.deviceType,
                                productsPerPage: getProductsPerPage(storeContext.preferences),
                                disableOrphanProducts,
                                enableVisualRelatedSearch,
                                enableDynamicVisNav,
                                pageType: getPageType(),
                                sponsoredgalleryData,
                                sponsoredProductSlotConfig,
                                enableSponsoredProduct,
                                enableSponsoredProductsOverflow,
                                enableMultipleThumbnails,
                                enableFourThumbnailsGrid,
                                isNative,
                            })
                        );
                        actions.push(
                            put({
                                type: GET_FILTERS_SUCCESS,
                                galleryDetails: galleryData,
                                isbloomReachAudience,
                                isBloomReachV3Page,
                                isConstructorSearchEnabled: useConstructor,
                            })
                        );
                    }
                }
                // Yield here.
                yield all(actions);

                yield put({
                    type: PREV_NO_RESULTS,
                    galleryDetails: galleryData,
                });

                yield call(refreshStoreInventory);

                if (!__SERVER__) {
                    yield fork(initiatePendingActions, {
                        payload: {
                            location: action.payload.loc,
                        },
                    });
                }
            }
        } else if (!__SERVER__) {
            logger.error({
                errorMessage: 'GalleryPage API timeout',
                exception: 'Timeout Error',
            });
            const errorDetails = [
                {
                    errorDescription: 'gallery Service timeout',
                },
            ];
            yield all([
                put({
                    type: AnalyticsActionTypes.FORM_ERROR,
                    errorDetails,
                }),
                put({
                    type: GET_GALLERY_DETAILS_TIMEOUT_ERROR,
                }),
                put({
                    type: HIDE_LOADER_GALLERY,
                }),
            ]);
        } else if (__SERVER__) {
            logger.error({
                errorMessage: 'GalleryPage API timeout',
                exception: 'Timeout Error',
            });
        }

        const enableABUserSegLogicFF = get(context.featureFlags, 'enableABUserSegLogic', false);

        if (enableABUserSegLogicFF && !isEmpty(action.payload.abTestParams)) {
            let cachedData = '';
            let usrsegStorageKey = '';
            let usersegval = '';
            // Get the UsrSeq value to invoke Endeca or Solr.
            const abtesttimeout = get(preferences, 'abTest.apiTimeOut', 5000); //eslint-disable-line
            const mbox = MboxHelper.getMboxValuesFromCookie();
            const mboxsession = mbox && mbox.session;
            // Getting the data which is cached and is in the local Storage. We need to pass the preferences object for this, which contains the cacheMinutes i.e. how long we need to store the object in localStorage, and flushTime - UTC milliseconds.
            cachedData = LocalStorage.getData('abTestUsrSeg', true);
            usersegval = cachedData && cachedData.createdTS ? '' : cachedData;
            // userseg will be stored part of local storage as a  {key: mboxsession , val: usersegval} format
            // If nothing is returned, make the service call and store in the localStorage.
            const parsedJson = usersegval && JSON.parse(usersegval);
            usersegval =
                parsedJson && parsedJson.val && parsedJson.key === mboxsession
                    ? parsedJson.val
                    : '';

            if (!usersegval) {
                const userSegResp = yield race({
                    usersegval: call(getABTestEngagementsSaga, {
                        apiPayload: action.payload.abTestParams,
                    }),
                    timeout: delay(abtesttimeout),
                });
                usersegval = userSegResp.usersegval && userSegResp.usersegval.UsrSeg;
                usrsegStorageKey = {
                    key: mboxsession,
                    val: usersegval,
                };
                usrsegStorageKey &&
                    LocalStorage.setData('abTestUsrSeg', usrsegStorageKey, true, '', false); // userseg localstorage expiration should be updated as per mbox unique identifier..inprogress
            }
            galleryparams = usersegval
                ? {
                      ...action.payload.params,
                      UsrSeg: usersegval,
                  }
                : action.payload.params;
        }
        if (enableBrStoreNullResultsMessage && LocalStorage.getData('galleryNoResults')) {
            const { storeNullResultsMessage = '' } = preferences;
            yield put({
                type: 'GET_STORE_NULL_RESULT_PAGE',
                noStores: storeNullResultsMessage,
            });

            LocalStorage.removeData('galleryNoResults');
        }
    } catch (error) {
        logger.error({
            errorMessage: 'GalleryPage load API call failed',
            exception: error,
        });
        if (!__SERVER__) {
            yield all([
                put({
                    type: GET_GALLERY_DETAILS_BY_ID_ERROR,
                    error,
                }),
                put({
                    type: HIDE_LOADER_GALLERY,
                }),
            ]);
        }
    }
    const { enableBrStoreNullResultsMessage = true } = yield select(getFeatureFlags);
    if (enableBrStoreNullResultsMessage && LocalStorage.getData('galleryNoResults')) {
        const { storeNullResultsMessage = '' } = preferences;
        yield put({
            type: 'GET_STORE_NULL_RESULT_PAGE',
            noStores: storeNullResultsMessage,
        });

        LocalStorage.removeData('galleryNoResults');
    }
}

function* getGalleryDetails({ payload }) {
    const {
        enableRealTimeInventory = true,
        enableLocationService,
        disableZoneInfoSevice,
        enableBloomReachV3PLP = false,
    } = yield select(getFeatureFlags);

    const { timeOutDelayForStoreLocation = 200 } = yield select(selectPreferences);
    const context = yield select(selectContext);
    const preferences = context.preferences || {};
    const { requestUrl = '' } = context;
    const { plpBloomreachInclusionList = [] } = preferences;
    const isBloomReachV3Page = enableBloomReachPage(
        enableBloomReachV3PLP,
        requestUrl,
        plpBloomreachInclusionList
    );
    const updatedPayload = {
        ...payload,
        isBloomReachV3Page,
    };

    // akamai will return the store details from location services
    // akamai caches the results which we don't want on the server side
    // location services are enabled only on client side for this reason
    // send store id on initial load when enableLocationService is enabled
    if (enableRealTimeInventory && enableLocationService) {
        let storeDetails = null;
        let userStore = yield select(getBOPISStore);
        const zipcode = yield select(getZipCode);
        const sessionZip = getZipInfo();
        const noResultsPage = LocalStorage.getData('NoResultsPage');
        if (userStore && userStore.storeId) {
            storeDetails = userStore;
        }

        if (initialLoad && !storeDetails) {
            userStore = yield race({
                success: takeMaybe(SET_USER_STORE_LOCATION_INFO),
                error: takeMaybe(LOAD_USER_STORE_LOCATION_INFO_ERROR),
                timeout: delay(timeOutDelayForStoreLocation),
            });

            if (userStore.success) {
                storeDetails = userStore.success.data;
            } else if (!disableZoneInfoSevice && shouldCallZoneAPI(zipcode, sessionZip)) {
                // retrive zone info
                const zoneInfo = yield race({
                    success: call(getZone, {
                        zipcode,
                    }),
                    timeout: delay(timeOutDelayForStoreLocation),
                });
                if (zoneInfo && zoneInfo.success) {
                    const zone = get(zoneInfo, 'success.data.zoneInfo.zone', '');
                    LocalStorage.setData('zone', {
                        zipcode,
                        zone,
                    });
                }
            }
        }

        // send store id which is saved in global var
        // Sending geoLocStoreId with every SOLR API request.
        const storeId = storeDetails && storeDetails.storeId;
        const zoneIformation = getZoneInfo();
        if (storeId) {
            if (
                updatedPayload?.params?.storeIds &&
                updatedPayload.params.storeIds !== storeId &&
                !noResultsPage
            ) {
                if (!__SERVER__ && !document.getElementById('storeGalleryBopisFilter')) {
                    updatedPayload.params.storeIds = storeId;
                }
            }
        } else if (zoneIformation && getPageType() === 's' && !disableZoneInfoSevice) {
            updatedPayload.params.Userzoneid = zoneIformation;
        }

        initialLoad = false;
    }
    yield fork(galleryDetailsSaga, {
        payload: updatedPayload,
    });
}

const watchInitiatePendingActionsRequest = function* watchInitiatePendingActionsRequest() {
    yield takeLatest(INITIATE_PENDING_ACTIONS, initiatePendingActions);
};
watchInitiatePendingActionsRequest.sagaName = 'watchInitiatePendingActionsRequest';

const watchGalleryDetailsRequest = function* watchGalleryDetailsRequest() {
    yield takeLatest(GET_GALLERY_DETAILS_BY_ID_REQUEST, getGalleryDetails);
};
watchGalleryDetailsRequest.sagaName = 'watchGalleryDetailsRequest';

export default watchGalleryDetailsRequest;
export { watchInitiatePendingActionsRequest };
